import { contoursToPath2D, type Contour } from './flatten';

export interface Raster {
  alpha: Uint8Array;
  width: number;
  height: number;
}

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

/** Prefers OffscreenCanvas so the same code path works inside a worker. */
export function createCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

export function get2d(canvas: AnyCanvas): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D {
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('2D-Kontext nicht verfügbar');
  return ctx;
}

/**
 * Rasterises glyph contours into an 8-bit coverage mask.
 *
 * The canvas rasteriser is used deliberately: it is the fastest correct scanline
 * filler available, it applies analytic antialiasing, and `nonzero` winding keeps
 * glyph counters (the hole in an "o") hollow.
 *
 * `supersample` renders at N× and box-filters down, which buys extra coverage
 * precision for the sub-pixel SDF refinement at small text sizes.
 */
export function rasteriseContours(
  contours: readonly Contour[],
  width: number,
  height: number,
  supersample = 1,
): Raster {
  const ss = Math.max(1, Math.round(supersample));
  const w = width * ss;
  const h = height * ss;

  const canvas = createCanvas(w, h);
  const ctx = get2d(canvas);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#fff';
  if (ss !== 1) ctx.scale(ss, ss);
  ctx.fill(contoursToPath2D(contours), 'nonzero');

  const image = ctx.getImageData(0, 0, w, h).data;
  const alpha = new Uint8Array(width * height);

  if (ss === 1) {
    for (let i = 0, p = 3; i < alpha.length; i++, p += 4) alpha[i] = image[p];
  } else {
    const inv = 1 / (ss * ss);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        for (let sy = 0; sy < ss; sy++) {
          const row = (y * ss + sy) * w;
          for (let sx = 0; sx < ss; sx++) sum += image[(row + x * ss + sx) * 4 + 3];
        }
        alpha[y * width + x] = Math.round(sum * inv);
      }
    }
  }

  return { alpha, width, height };
}
